import React, { useCallback, useEffect, useRef, useState } from 'react';
import InterestFitConfirmation from './InterestFitConfirmation';

/**
 * Await this before recording interest.
 *
 *   const { requestConfirmation, confirmationDialog } = useInterestFitConfirmation();
 *   ...
 *   if (!(await requestConfirmation({ audience: 'company', providerProfileId, listingId }))) return;
 *
 * Resolves true only when the person pressed the confirm button with the fit
 * report (or its honest "could not compare" state) in front of them. Cancel,
 * Escape, backdrop, unmount, or a second request while one is open all
 * resolve false, so a caller can never record interest by accident.
 *
 * The host MUST render `confirmationDialog`.
 */
export default function useInterestFitConfirmation() {
  const [pending, setPending] = useState(null);
  const pendingRef = useRef(null);

  const requestConfirmation = useCallback((request) => {
    if (pendingRef.current || !request) return Promise.resolve(false);
    const audience = request.audience === 'company' ? 'company' : 'provider';
    const listingId = request.listingId ? String(request.listingId) : '';
    const providerProfileId = request.providerProfileId
      ? String(request.providerProfileId)
      : '';
    if (!listingId) return Promise.resolve(false);
    if (audience === 'company' && !providerProfileId) return Promise.resolve(false);
    return new Promise((resolve) => {
      const entry = {
        audience,
        listingId,
        providerProfileId,
        subject: request.subject || null,
        // Company decks hand the engagement gate's refusal on to their own
        // gate panel, so a refused pair does not loop on the same card.
        onRefusal: typeof request.onRefusal === 'function' ? request.onRefusal : null,
        resolve,
      };
      pendingRef.current = entry;
      setPending(entry);
    });
  }, []);

  const finish = useCallback((confirmed) => {
    const entry = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    entry?.resolve(confirmed === true);
  }, []);

  const cancelPending = useCallback(() => {
    if (pendingRef.current) finish(false);
  }, [finish]);

  useEffect(
    () => () => {
      pendingRef.current?.resolve(false);
      pendingRef.current = null;
    },
    [],
  );

  return {
    requestConfirmation,
    cancelPending,
    isConfirming: !!pending,
    confirmationDialog: pending ? (
      <InterestFitConfirmation
        audience={pending.audience}
        listingId={pending.listingId}
        providerProfileId={pending.providerProfileId}
        subject={pending.subject}
        onRefusal={pending.onRefusal || undefined}
        onCancel={() => finish(false)}
        onConfirm={() => finish(true)}
      />
    ) : null,
  };
}
